import { Inject, Injectable, Input } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subscription, timer } from 'rxjs';

import { Api } from '../api/api';
import { GisToolsApi } from '../api/gis-tools-api';
import { environment } from '../environment';
import { FirstOrLast, IVideoLogImage, LogLevel, VideoLogAction, VideoLogPlayerState } from '../types';

import { AoiService } from './aoi.service';
import { ILoggingService } from './interfaces/ILoggingService';

const SECOND = 1000;
const OLDYEAR = 2017;
const OPTIONS = {
    atCountyBoundary: 'deny' as VideoLogAction,
    atSegmentBoundary: 'continue' as VideoLogAction,
    baseUrl: environment.videoLogBaseUrl,
    imagesLocation: environment.videoLogImagesLocation,
    imageExtension: '.jpg',
    serverFolder: '/thumbnails',
    roadware1: '1',
    routeLeftImageIdentifier: '10',
    routeRightImageIdentifier: '20',
    routeCenterImageIdentifier: '00',
    rampsFolder: '/ramps',
    rampCenterImageIdentifier: '3C',
    rampLeftImageIdentifier: '1L',
    rampRightImageIdentifier: '2S'
};
@Injectable({
    providedIn: 'root'
})
export class VideoLogService {
    atSegmentBoundary: VideoLogAction = OPTIONS.atSegmentBoundary;
    atCountyBoundary: VideoLogAction = OPTIONS.atCountyBoundary;
    showHistoricVideoLog: boolean = false;
    crsSelection: boolean = false;
    findinxlist: boolean = false;
    yearRange: any;
    currentYearIndex: number;
    private _currentYear: number;
    get currentYear(): number {
        return this._currentYear;
    }
    set currentYear(year: number) {
        if (this.videoLog && this.image && this.currentYear !== year) {
            this._currentYear = year;
            if (this.yearRange && this.yearRange.years.includes(year)) {
                this.currentYearIndex = this.yearRange.years.indexOf(year);
            } else if (this.yearRange && !this.yearRange.years.includes(year)) {
                this.currentYearIndex = this.yearRange.yearIndexMax;
            } else {
                this.currentYearIndex = null;
            }
            this.goToSegmentAndSetOffset(this.videoLog.countyCode, this.videoLog.stateRouteNumber, this.videoLog.segmentNumber, this.image.offset).then(() => {
                this.refreshViewer.next();
            });
        } else {
            this._currentYear = year;
            if (this.yearRange && this.yearRange.years.includes(year)) {
                this.currentYearIndex = this.yearRange.years.indexOf(year);
            } else if (this.yearRange && !this.yearRange.years.includes(year)) {
                this.currentYearIndex = this.yearRange.yearIndexMax;
            } else {
                this.currentYearIndex = null;
            }
        }
    }

    videoLog: IVideoLogResult;
    videoLogUrl: string;
    images: IVideoLogImage[];
    image: IVideoLogImage;
    imageServerFolder: string = OPTIONS.serverFolder;

    refreshSlider: ReplaySubject<number> = new ReplaySubject<number>();
    refreshInfo: ReplaySubject<void> = new ReplaySubject<void>();
    refreshViewer: ReplaySubject<void> = new ReplaySubject<void>();
    segmentEnd: ReplaySubject<FirstOrLast> = new ReplaySubject<FirstOrLast>(1);
    countyEnd: ReplaySubject<FirstOrLast> = new ReplaySubject<FirstOrLast>(1);
    routeEnd: ReplaySubject<FirstOrLast> = new ReplaySubject<FirstOrLast>(1);
    newOffset: ReplaySubject<any> = new ReplaySubject<any>(1);
    newPlayerState: BehaviorSubject<VideoLogPlayerState> = new BehaviorSubject<VideoLogPlayerState>('stop');
    panelsSwapped: ReplaySubject<boolean> = new ReplaySubject<boolean>();
    openThreeImageView: ReplaySubject<boolean> = new ReplaySubject<boolean>();
    imagedToggled: ReplaySubject<'Left' | 'Center' | 'Right'> = new ReplaySubject<'Left' | 'Center' | 'Right'>();
    imageToggleButtonsReset: ReplaySubject<void> = new ReplaySubject<void>();

    fromClick: boolean = false;
    segHighlightEnabled: boolean = false;
    keyboardEnabled: boolean = false;
    playerSetting = { minSpeed: 0.1, maxSpeed: 2, step: 0.1, defaultSpeed: 0.5 };
    playerState: VideoLogPlayerState = 'stop';
    playerTimerSub: Subscription;
    private _playerSpeed: number = Math.min(Math.max(this.playerSetting.defaultSpeed, this.playerSetting.minSpeed), this.playerSetting.maxSpeed);
    get playerSpeed(): number {
        return this._playerSpeed;
    }
    set playerSpeed(value: number) {
        this._playerSpeed = Math.min(Math.max(value, this.playerSetting.minSpeed), this.playerSetting.maxSpeed);
        if (this.playerState === 'play') {
            this.playerTimerSub.unsubscribe();
            this.playerTimerSub = timer(0, this.playerSpeed * SECOND).subscribe(this.goToNextOffset.bind(this));
        }
    }

    // Min: 0 (zoomed out), Max: 23 (zoomed in)
    private _followZoomLevel: number = 18;
    get followZoomLevel(): number {
        return this._followZoomLevel;
    }
    set followZoomLevel(value: number) {
        if (value > 23) { value = 23; }
        if (value < 0) { value = 0; }
        this._followZoomLevel = value;
    }

    private _zoomOnPositionChange: ZoomOnPositionChangeType = 'zoom';
    get zoomOnPositionChange(): ZoomOnPositionChangeType { return this._zoomOnPositionChange; }
    set zoomOnPositionChange(val: ZoomOnPositionChangeType) {
        if (typeof val === 'string') {
            val = val.toLocaleLowerCase().trim() as ZoomOnPositionChangeType;
        }
        this._zoomOnPositionChange = val;
    }

    get canGoToFirstOffset() {
        return this.image && this.image.sequenceNumber !== undefined && this.image.sequenceNumber > 0;
    }

    get canGoToLastOffset() {
        return this.image && this.image.sequenceNumber !== undefined && this.image.sequenceNumber < (this.images.length - 1);
    }

    get canGoToPreviousOffset() {
        return this.canGoToFirstOffset || this.canGoToPreviousSegment;
    }

    get canGoToNextOffset() {
        return this.canGoToLastOffset || this.canGoToNextSegment;
    }

    get canGoToPreviousSegment() {
        if (this.videoLog && this.videoLog.previousSegmentNumber && this.videoLog.previousCountyCode) {
            return this.videoLog.previousCountyCode === this.videoLog.countyCode ? this.atSegmentBoundary !== 'deny' : this.atCountyBoundary !== 'deny';
        } else {
            return false;
        }
    }

    get canGoToNextSegment() {
        if (this.videoLog && this.videoLog.nextSegmentNumber && this.videoLog.nextCountyCode) {
            return this.videoLog.nextCountyCode === this.videoLog.countyCode ? this.atSegmentBoundary !== 'deny' : this.atCountyBoundary !== 'deny';
        } else {
            return false;
        }
    }

    get canCrossSegments() {
        return this.atSegmentBoundary !== 'deny';
    }

    get canReverseDirection() {
        return this.videoLog && this.videoLog.oppositeSegmentNumber;
    }

    get isPromptAtBoundary() {
        return this.atSegmentBoundary === 'prompt';
    }

    constructor(
        private readonly api: Api,
        private readonly gisToolsApi: GisToolsApi,
        private readonly aoiService: AoiService,
        @Inject('LoggingService') public LoggingService: ILoggingService
    ) { }

    public resetVideoLog() {
        this.videoLog = null;
        this.videoLogUrl = null;
        this.image = null;
        this.yearRange = null;
        this.currentYear = null;
        this.newOffset.next(null);
    }

    public toggleHistoricVideoLog() {
        this.showHistoricVideoLog = !this.showHistoricVideoLog;
    }

    public toggleSegmentHighlight() {
        this.segHighlightEnabled = !this.segHighlightEnabled;

        if (this.segHighlightEnabled) {
            if (this.image) {
                this.highlightSegment();
            }
        } else {
            //remove any existing highlighted segment
            this.aoiService.clearSearchCriterias();
            this.aoiService.showBySearchCriterias(false, false, false);
        }
    }

    public toggleCrsSelection() {
        this.crsSelection = !this.crsSelection;
    }

    public togglefindinxlist() {
        this.findinxlist = !this.findinxlist;
    }

    public toggleKeyboardEnabled(enabled?: boolean) {
        this.keyboardEnabled = enabled !== null && enabled !== undefined ? enabled : !this.keyboardEnabled;
    }

    public async goToSegmentAndSetOffset(county, route, segment, offset: string | FirstOrLast, fromClick = false) {
        await this.goToSegment(county, route, segment);
        if (offset === 'first') {
            this.changeCurrentOffset(0, fromClick);
        } else if (offset === 'last') {
            this.changeCurrentOffset(this.images.length - 1, fromClick);
        } else {
            await this.goToOffset(offset, fromClick);
        }
    }

    public async goToSegment(county, route, segment) {
        const year = this.currentYear && this.currentYearIndex !== this.yearRange.yearIndexMax ? this.currentYear.toString() : '0';
        const result: IVideoLogResult[] = await this.gisToolsApi.getVideoLogForSegment(county, route, segment, year);
        this.videoLog = result.length > 0 ? result[0] : null;
        this.videoLog.segmentNumber = this.videoLog && this.videoLog.streetName.toLowerCase().includes('ramp') ? `${this.videoLog.segmentNumber} ${this.videoLog.streetName}` : this.videoLog.segmentNumber;

        if (this.videoLog && this.videoLog.yearRange) {
            const years = this.videoLog.yearRange.map(x => parseInt(x, 10));
            this.yearRange = {
                years,
                yearIndexMax: years.length - 1,
                yearIndices: Array.from(years.keys())
            };
            if (!this.currentYearIndex || this.currentYearIndex > this.yearRange.yearIndexMax) {
                this.currentYearIndex = this.yearRange.yearIndexMax;
            }
        }

        if (this.videoLog && this.videoLog.images) {
            this.images = this.videoLog.images.map((i, idx) => ({
                fileName: i.fileName,
                imageDate: i.imageDate,
                imageName1: i.imageName1,
                imageName2: i.imageName2,
                imageName3: i.imageName3,
                latitude: i.latitude,
                longitude: i.longitude,
                offset: i.offset,
                sequenceNumber: idx
            } as IVideoLogImage));
        }
        if (this.currentYearIndex) {
            this.refreshSlider.next(this.currentYearIndex);
        }

        if (this.segHighlightEnabled && !this.aoiService.searchCriterias) {
            this.highlightSegment();
        } else if (this.segHighlightEnabled && this.aoiService.searchCriterias && this.aoiService.searchCriterias.length > 0
            && !(parseInt(this.videoLog.segmentNumber, 10) >= parseInt(this.aoiService.searchCriterias[0].segBgn, 10)
            && parseInt(this.videoLog.segmentNumber, 10) <= parseInt(this.aoiService.searchCriterias[0].segEnd, 10))) {

            this.highlightSegment();
        }

        this.refreshInfo.next();
    }

    public async goToOffset(offset: string = '0', fromClick = false) {
        if (this.images && this.images.length > 0) {
            const targetOffset = parseInt(offset, 10);
            const offsetIndexes = this.images.map((image, i) => ({ originalIndex: i, offset: parseInt(image.offset, 10) }));
            offsetIndexes.sort((a, b) => {
                if (a.offset > b.offset) {
                    return 1;
                } else if (a.offset < b.offset) {
                    return -1;
                }
                return 0;
            });
            const exact = offsetIndexes.find(i => i.offset === targetOffset);
            let arrayIndex = offsetIndexes[0].originalIndex;
            // TODO: Get the image based on if the offset is within the average difference of offsets between image (so get the closest image)
            if (!exact) {
                for (let i = 0; i < this.images.length; i++) {
                    const image = this.images[i];
                    if (parseInt(image.offset, 10) === targetOffset) {
                        arrayIndex = i;
                        break;
                    } else if (this.videoLog && this.videoLog.sideIndicator !== null &&
                        ((this.videoLog.sideIndicator === '1' && parseInt(image.offset, 10) > targetOffset) ||
                         (this.videoLog.sideIndicator !== '1' && parseInt(image.offset, 10) < targetOffset))) {
                        arrayIndex = (i > 0) ? i - 1 : 0;
                        break;
                    }
                }
            } else {
                arrayIndex = exact.originalIndex;
            }

            this.changeCurrentOffset(arrayIndex, fromClick);
        }
    }

    public togglePromptAtBoundary() {
        if (this.isPromptAtBoundary) {
            this.atCountyBoundary = 'continue';
            this.atSegmentBoundary = 'continue';
        } else {
            this.atCountyBoundary = 'prompt';
            this.atSegmentBoundary = 'prompt';
        }
    }

    public goToFirstOffset() {
        if (this.canGoToFirstOffset) {
            this.changeCurrentOffset(0);
        }
    }

    public goToLastOffset() {
        if (this.canGoToLastOffset) {
            this.changeCurrentOffset(this.images.length - 1);
        }
    }

    public goToPreviousOffset() {
        if (this.image && this.image.sequenceNumber >= 0) {
            if (this.image && this.image.sequenceNumber > 0) {
                this.changeCurrentOffset(this.image.sequenceNumber - 1);
            } else if (this.isPromptAtBoundary) {
                if (this.playerState === 'play') {
                    this.changePlayerState('pause');
                }
                this.segmentEnd.next('first');
            } else {
                this.goToPreviousSegment();
            }
        }
    }

    public goToNextOffset() {
        if (this.images && this.images.length > 0) {
            if (this.image && this.image.sequenceNumber < this.images.length - 1) {
                this.changeCurrentOffset(this.image.sequenceNumber + 1);
            } else if (this.isPromptAtBoundary) {
                this.changePlayerState('pause');
                this.segmentEnd.next('last');
            } else {
                this.goToNextSegment();
            }
        }
    }

    public async goToPreviousSegment() {
        if (this.canGoToPreviousSegment) {
            if (this.videoLog.previousCountyCode !== this.videoLog.countyCode && this.atCountyBoundary === 'prompt') {
                this.changePlayerState('pause');
                this.countyEnd.next('first');
            } else {
                try {
                    await this.goToSegmentAndSetOffset(this.videoLog.previousCountyCode, this.videoLog.stateRouteNumber, this.videoLog.previousSegmentNumber, 'last');
                } catch (error) {
                    this.LoggingService.Log(error, LogLevel.Error);
                }
            }
        } else {
            this.changePlayerState('stop');
            this.routeEnd.next('first');
        }
    }

    public async goToNextSegment() {
        if (this.canGoToNextSegment) {
            if (this.videoLog.nextCountyCode !== this.videoLog.countyCode && this.atCountyBoundary === 'prompt') {
                this.changePlayerState('pause');
                this.countyEnd.next('last');
            } else {
                try {
                    await this.goToSegmentAndSetOffset(this.videoLog.nextCountyCode, this.videoLog.stateRouteNumber, this.videoLog.nextSegmentNumber, 'first');
                } catch (error) {
                    this.LoggingService.Log(error, LogLevel.Error);
                }
            }
        } else {
            this.changePlayerState('stop');
            this.routeEnd.next('last');
        }
    }

    public async goToPreviousCounty() {
        if (this.canGoToPreviousSegment) {
            try {
                await this.goToSegmentAndSetOffset(this.videoLog.previousCountyCode, this.videoLog.stateRouteNumber, this.videoLog.previousSegmentNumber, 'last');
            } catch (error) {
                this.LoggingService.Log(error, LogLevel.Error);
            }
        }
    }

    public async goToNextCounty() {
        if (this.canGoToNextSegment) {
            try {
                await this.goToSegmentAndSetOffset(this.videoLog.nextCountyCode, this.videoLog.stateRouteNumber, this.videoLog.nextSegmentNumber, 'first');
            } catch (error) {
                this.LoggingService.Log(error, LogLevel.Error);
            }
        }
    }

    public async reverseDirection() {
        try {
            const oppositeOffset = await this.gisToolsApi.getOppositeOffset(this.videoLog.countyCode, this.videoLog.stateRouteNumber, this.videoLog.juris, this.videoLog.segmentNumber, this.image.offset);
            if (oppositeOffset) {
                await this.goToSegmentAndSetOffset(oppositeOffset.countyCode, oppositeOffset.stateRouteNumber, oppositeOffset.segmentNumber, oppositeOffset.offset);
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        }
    }

    public startOrStopVideo() {
        if (this.playerState === 'play') {
            this.changePlayerState('stop');
        } else {
            this.changePlayerState('play');
        }
    }

    public resumeVideo() {
        if (this.playerState === 'pause') {
            this.changePlayerState('play');
        }
    }

    public stopVideo() {
        this.changePlayerState('stop');
    }

    @Input() public toggleFullsizeImages(fullsize?: boolean) {
        if (fullsize === undefined || fullsize === null) {
            this.imageServerFolder = this.imageServerFolder === '/fullsize' ? '/thumbnails' : '/fullsize';
        } else {
            this.imageServerFolder = fullsize ? '/fullsize' : '/thumbnails';
        }
        if (this.images) {
            this.images.forEach(image => {
                image.url = undefined;
                this.generateImageUrl(image);
            });
        }
    }

    public get isFullSize() { return this.imageServerFolder === '/fullsize'; }

    public async crsHasYear(crs, year: string): Promise<boolean> {
        const result: IVideoLogResult[] = await this.gisToolsApi.getVideoLogForSegment(crs.countyCode, crs.stateRouteNumber, crs.segmentNumber, year);
        return result.length > 0;
    }

    public async downloadImages() {
        const imageCount = 15;
        const imageList = this.images.slice(this.image.sequenceNumber, this.image.sequenceNumber + imageCount);
        imageList.forEach(image => {
            this.generateImageUrl(image);
        });

        const data = imageList.map(image => {

            if (image.imageName2 === '' || image.imageName3 === '') {
                return [
                    this.videoLog.countyCode,
                    this.videoLog.stateRouteNumber,
                    this.videoLog.segmentNumber,
                    image.offset,
                    image.imageName1,
                    image.url
                ];
            } else {
                return [
                    this.videoLog.countyCode,
                    this.videoLog.stateRouteNumber,
                    this.videoLog.segmentNumber,
                    image.offset,
                    image.imageName3, // left
                    image.imageName1, // center
                    image.imageName2, // right
                    image.lurl,
                    image.url,
                    image.rurl
                ];
            }
        });

        let url = '';
        let leftUrl = '';
        let rightUrl = '';
        url += `/${this.videoLog.year}`;
        url += `/PA${this.videoLog.year}CO${this.videoLog.countyCode}`;

        let strSubFolder = this.getSubFolder(this.image);
        if (typeof (strSubFolder) === 'string') {
            strSubFolder = strSubFolder.split(',');
        }

        if (this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'row') &&
            this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardleft') &&
            this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardright')) {
            leftUrl = `${url}/${this.videoLog.rootFolder}/ForwardLeft/`;
            rightUrl = `${url}/${this.videoLog.rootFolder}/ForwardRight/`;
            url += `/${this.videoLog.rootFolder}/ROW/`;
        } else if (strSubFolder === undefined || strSubFolder[1] === '') {
            leftUrl = `${url}/${this.videoLog.rootFolder}/`;
            rightUrl = `${url}/${this.videoLog.rootFolder}/`;
            url += `/${this.videoLog.rootFolder}/`;
        } else if (this.currentYear > OLDYEAR) {
            url += `/${strSubFolder[0]}/`;
            leftUrl += `/${strSubFolder[0]}/`;
            rightUrl += `/${strSubFolder[0]}/`;
        } else {
            leftUrl = `${url}/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeLeftImageIdentifier}`;
            rightUrl = `${url}/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeRightImageIdentifier}`;
            url += `/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeCenterImageIdentifier}`;

            url += `/${strSubFolder[1]}/`;
            leftUrl += `/${strSubFolder[1]}/`;
            rightUrl += `/${strSubFolder[1]}/`;
        }

        let cn: string[] = [];
        let bp: {};
        if (url !== leftUrl) {
            cn = ['county', 'route', 'segment', 'offset', 'left image name', 'center image name', 'right image name', 'left url ', 'center url', 'right url'];
            bp = { url, leftUrl, rightUrl };
        } else {
            cn = ['county', 'route', 'segment', 'offset', 'left image name', 'center image name', 'right image name', 'url'];
            bp = { url };
        }

        const imageData = {
            title: '15_Offset_Images',
            columnNames: cn,
            data,
            basePaths: bp
        };

        try {
            const filename = await this.api.http2.post('videolog/images', imageData, { responseType: 'text' });
            if (filename) {
                window.open([this.api.http2.url, 'videolog/file/', filename].join(''), '_blank');
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        }
    }

    public swapPanelUpdate(showThreeImages: boolean) {
        this.panelsSwapped.next(showThreeImages);
    }
    public toggleImage(imageName: 'Left' | 'Center' | 'Right') {
        this.imagedToggled.next(imageName);
    }
    public resetImageToggleButtons() {
        this.imageToggleButtonsReset.next();
    }

    private changeCurrentOffset(imageIndex, fromClick = false) {
        if (imageIndex > -1 && this.images && imageIndex < this.images.length) {
            this.fromClick = fromClick;
            const image = this.images[imageIndex];

            this.generateVideoLogUrl(this.videoLog.countyCode, this.videoLog.stateRouteNumber, this.videoLog.segmentNumber, image.offset);
            this.generateImageUrl(image);

            if (window.location.href.includes('isPopUp=true') && window.location.href.includes('pos=') && image.url) {
                /* In a pop out window, continues to use the left or right image when user advances */
                const nextChar = window.location.href.charAt(window.location.href.indexOf('pos=') + 'pos='.length);
                if (nextChar === 'l') {
                    image.url = image.lurl;
                } else if (nextChar === 'r') {
                    image.url = image.rurl;
                }
            }

            this.setNewOffsetImage(image);
        }
    }

    private setNewOffsetImage(image) {
        this.image = image;
        this.newOffset.next(image);
    }

    private changePlayerState(suggestedState: VideoLogPlayerState) {
        switch (suggestedState) {
            case 'play':
                if (this.playerState === 'stop' || this.playerState === 'pause') {
                    this.playerState = suggestedState;
                    this.newPlayerState.next(this.playerState);
                    this.playerTimerSub = timer(0, this.playerSpeed * SECOND).subscribe(this.goToNextOffset.bind(this));
                }
                break;

            case 'stop':
                if (this.playerState === 'play') {
                    this.playerState = suggestedState;
                    this.newPlayerState.next(this.playerState);
                    this.playerTimerSub.unsubscribe();
                } else if (this.playerState === 'pause') {
                    this.playerState = suggestedState;
                    this.newPlayerState.next(this.playerState);
                }
                break;

            case 'pause':
                if (this.playerState === 'play') {
                    this.playerState = suggestedState;
                    this.newPlayerState.next(this.playerState);
                    this.playerTimerSub.unsubscribe();
                }
                break;
            default:
                break;
        }
    }

    private generateVideoLogUrl(county, route, segment, offset) {
        let videoLogUrl = OPTIONS.baseUrl;
        if (county && route && segment) {
            videoLogUrl += `?cn=${county}&rt=${route}&sg=${segment}`;
            if (offset) {
                videoLogUrl += `&offset=${offset}`;
            }
        }
        this.videoLogUrl = videoLogUrl;
    }

    private generateImageUrl(image) {
        let imageLeftUrl = '';
        let imageRightUrl = '';
        if (!image.url) {
            if (this.videoLog && this.videoLog.stateRouteNumber && this.videoLog.rootFolder) {
                let imageUrl = OPTIONS.imagesLocation + this.imageServerFolder;
                if (this.isRamp(this.videoLog.stateRouteNumber)) {
                    imageUrl += `/${OPTIONS.rampsFolder}`;
                    imageUrl += this.videoLog.rootFolder;
                    imageLeftUrl = imageUrl + OPTIONS.rampLeftImageIdentifier;
                    imageRightUrl = imageUrl + OPTIONS.rampRightImageIdentifier;
                    imageUrl += OPTIONS.rampCenterImageIdentifier;
                } else {
                    imageUrl += `/${this.videoLog.year}`;
                    imageUrl += `/PA${this.videoLog.year}CO${this.videoLog.countyCode}`;

                    let strSubFolder = this.getSubFolder(image);
                    if (typeof (strSubFolder) === 'string') {
                        strSubFolder = strSubFolder.split(',');
                    }

                    if (this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'row') &&
                        this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardleft') &&
                        this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardright')) {
                        imageLeftUrl = `${imageUrl}/${this.videoLog.rootFolder}/ForwardLeft/`;
                        imageRightUrl = `${imageUrl}/${this.videoLog.rootFolder}/ForwardRight/`;
                        imageUrl += `/${this.videoLog.rootFolder}/ROW/`;
                        //open 3 image view if it is not already
                        if (this.fromClick) {
                            this.openThreeImageView.next(true);
                        }
                    } else if (strSubFolder === undefined || strSubFolder[1] === '') {
                        imageLeftUrl = `${imageUrl}/${this.videoLog.rootFolder}/`;
                        imageRightUrl = `${imageUrl}/${this.videoLog.rootFolder}/`;
                        imageUrl += `/${this.videoLog.rootFolder}/`;
                        if (this.fromClick) {
                            this.openThreeImageView.next(false);
                        }
                    } else if (this.currentYear > OLDYEAR) {
                        imageUrl += `/${strSubFolder[0]}/`;
                        imageLeftUrl += `/${strSubFolder[0]}/`;
                        imageRightUrl += `/${strSubFolder[0]}/`;
                        if (this.fromClick) {
                            this.openThreeImageView.next(false);
                        }
                    } else {
                        imageLeftUrl = `${imageUrl}/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeLeftImageIdentifier}`;
                        imageRightUrl = `${imageUrl}/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeRightImageIdentifier}`;
                        imageUrl += `/${OPTIONS.roadware1}${strSubFolder[0]}${OPTIONS.routeCenterImageIdentifier}`;

                        imageUrl += `/${strSubFolder[1]}/`;
                        imageLeftUrl += `/${strSubFolder[1]}/`;
                        imageRightUrl += `/${strSubFolder[1]}/`;
                    }
                }

                imageUrl += `${image.imageName1}${OPTIONS.imageExtension}`;
                imageLeftUrl += `${image.imageName3}${OPTIONS.imageExtension}`;
                imageRightUrl += `${image.imageName2}${OPTIONS.imageExtension}`;

                image.url = imageUrl;
                image.lurl = imageLeftUrl;
                image.rurl = imageRightUrl;

                if (this.videoLog.isHighDef) {
                    image.lurl = imageUrl;
                    image.rurl = imageUrl;
                }

                image.isHighDef = this.videoLog.isHighDef;
            }
        }
    }

    private isRamp(route) {
        return route >= '8000' && route <= '8999';
    }

    private getSubFolder(image) {
        let subFolder = image.imageName1;
        const imageNumber = parseInt(subFolder, 10);
        let lstSubFolders = [];

        if (!isNaN(imageNumber)) {
            if (this.videoLog.subFolders !== undefined && this.videoLog.subFolders instanceof Array) {

                if (this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'row') &&
                    this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardleft') &&
                    this.videoLog.subFolders.find(sf => sf.toLowerCase() === 'forwardright')) {
                    return this.videoLog.subFolders;
                } else if (this.videoLog.subFolders[0] === '') {
                    subFolder = '';
                } else {
                    const additionalFolder = this.videoLog.additionalFolders.find(x => x.rootFolder === this.videoLog.rootFolder);
                    if (additionalFolder) {
                        lstSubFolders = additionalFolder.subFolders;
                    }

                    for (const lstSubFolder of lstSubFolders) {
                        const folderNumber = parseInt(lstSubFolder, 10);
                        if (!isNaN(folderNumber)) {
                            if (folderNumber > imageNumber) {
                                break;
                            }
                            subFolder = lstSubFolder;
                        }
                    }
                }
            }
        }

        return `${this.videoLog.rootFolder},${subFolder}`;
    }

    private async highlightSegment() {
        const searchCriteria: any = {
            type: 'route',
            title: `Route (${this.videoLog.stateRouteNumber})`,
            stateRouteNumber: this.videoLog.stateRouteNumber
        };
        if (this.videoLog.countyCode) {
            searchCriteria.title += `County (${this.videoLog.countyCode})`;
            searchCriteria.countyCode = this.videoLog.countyCode;
        }
        if (this.videoLog.segmentNumber) {
            searchCriteria.title = `${searchCriteria.title} ${this.videoLog.segmentNumber}-${this.videoLog.nextSegmentNumber}`;
            searchCriteria.segBgn = this.videoLog.segmentNumber;
            searchCriteria.segEnd = this.videoLog.segmentNumber;
        }
        this.aoiService.setSearchCriterias([searchCriteria]);

        let forcedStop = false;
        if (this.playerState === 'play') {
            this.startOrStopVideo();
            forcedStop = true;
        }
        await this.aoiService.showBySearchCriterias(true, false, false);
        if (forcedStop && this.playerState === 'stop') {
            this.startOrStopVideo();
        }
    }
}

interface IVideoLogResult {
    countyCode: string;
    countyName: string;
    stateRouteNumber: string;
    routeName: string;
    direction: string;
    sideIndicator: string;
    juris: string;
    segmentNumber: string;
    streetName: string;
    oppositeSegmentNumber: string;
    previousCountyCode: string;
    previousSegmentNumber: string;
    nextSegmentNumber: string;
    nextCountyCode: string;
    year: string;
    yearRange?: string[];
    rootFolder: string;
    subFolders?: string[];
    additionalFolders?: IAdditionalFoldersEntity[];
    isHighDefinition: string;
    images?: IImagesEntity[];
    isHighDef?: boolean;
}
interface IAdditionalFoldersEntity {
    rootFolder: string;
    subFolders?: string[];
}
interface IImagesEntity {
    imageName1: string;
    imageName2: string;
    imageName3: string;
    imageDate: string;
    latitude: number;
    longitude: number;
    offset: string;
    fileName: string;
    sequenceNumber: string;
}

export type ZoomOnPositionChangeType = 'none' | 'follow' | 'zoom' | 'first';
